import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  apiErrorMessage, apiGet, apiPatch, getTask, rollForwardTask,
  getTaskFiles, getTimeEntriesForTask, createTaskFile, deleteTaskFile,
  getClientsInGroup,
} from '../api';
import useClientRollup from '../hooks/useClientRollup';
import { tileDrilldown } from '../utils/rollup';
import {
  BreakdownByClientTable, DetailModal, GroupDistributionDonut, IncompleteDataBanner, MetricTiles,
  SummaryByYearTable, TimeByCodePie, TimeByEmployeePie, ViewModeToggle,
} from '../components/rollup';
import SharePointFilePicker from '../components/SharePointFilePicker';
import TimelineDrawer from '../components/TimelineDrawer';
import RollForwardFlow from './tasks/RollForwardFlow';
import TimeLockControl from './tasks/TimeLockControl';
import TaskNameEditor from './tasks/TaskNameEditor';

const TABS = ['List', 'Files', 'Time Tracking'];

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [statusOptions, setStatusOptions] = useState({ general: [], tax_filing: [] });
  const [tab, setTab] = useState('List');
  const [files, setFiles] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // True while the roll-forward modal (RollForwardFlow) is open. The modal
  // owns its own requests, including the 409 unapproved-time case.
  const [rollPrompt, setRollPrompt] = useState(false);

  const [viewMode, setViewMode] = useState('client');       // 'client' | 'group'
  const [groupMetric, setGroupMetric] = useState('time');
  const [detailModal, setDetailModal] = useState(null);
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [groupMembersFailed, setGroupMembersFailed] = useState(false);

  const reloadTask = () => getTask(id).then(setTask).catch(e => setError(e.message || 'Failed to load task'));
  const reloadFiles = () => getTaskFiles(id).then(setFiles).catch(() => setFiles([]));

  useEffect(() => { reloadTask(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiGet('/status-options/by_type/')
      .then(d => setStatusOptions({ general: d.general || [], tax_filing: d.tax_filing || [] }))
      .catch(() => setStatusOptions({ general: [], tax_filing: [] }));
  }, []);

  useEffect(() => {
    if (!id) return;
    if (tab === 'Files') reloadFiles();
    else if (tab === 'Time Tracking') getTimeEntriesForTask(id).then(setTimeEntries).catch(() => setTimeEntries([]));
  }, [id, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!task?.client_group) {
      setGroupMemberIds([]);
      setGroupMembersFailed(false);
      setViewMode('client');
      return;
    }
    setGroupMembersFailed(false);
    getClientsInGroup(task.client_group)
      .then((members) => setGroupMemberIds(members.map((m) => m.id)))
      .catch(() => {
        // Don't let this look like a small group: the toggle still renders
        // (it only checks group_name) and rollupIds silently falls back to
        // single-client data, which reads as a legitimate — just small —
        // group roll-up. Flag it instead so the failure is visible.
        setGroupMemberIds([]);
        setGroupMembersFailed(true);
      });
  }, [task?.client_group]);

  const rollupIds = useMemo(() => {
    if (viewMode === 'group' && groupMemberIds.length) return groupMemberIds;
    return task?.client ? [task.client] : [];
  }, [viewMode, groupMemberIds, task?.client]);

  const rollup = useClientRollup(rollupIds);

  const computeStatusSideEffects = (field, newValueId) => {
    const opts =
      field === 'status' ? statusOptions.general :
      field === 'gst_status' ? statusOptions.tax_filing :
      null;
    if (!opts || !newValueId) return {};
    const opt = opts.find((o) => o.id === newValueId);
    if (!opt) return {};
    const name = (opt.name || '').toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    if (field === 'status' && name.includes('received') && name.includes('doc')) {
      return { docs_received_date: today };
    }
    if (field === 'status' && name === 'filed') {
      return task.task_category === 'personal'
        ? { efile_date: today }
        : { efiled_date: today };
    }
    return {};
  };

  const patchField = async (field, value) => {
    const extras = computeStatusSideEffects(field, value);
    // Choosing a "Completed" status finishes the file exactly as the
    // checkmark does: the boolean goes with it (the server refuses to roll an
    // open task forward), and once saved the roll-forward is offered. It used
    // to set the status alone and stop.
    const completing =
      field === 'status' && !task.is_completed &&
      statusOptions.general.some(
        (o) => o.id === value && (o.name || '').trim().toLowerCase() === 'completed');
    if (completing) extras.is_completed = true;
    const payload = { [field]: value, ...extras };
    setSaving(true);
    try {
      await apiPatch(`/api/tasks/${id}/`, payload);
      await reloadTask();
      // Opened even with no fiscal year: the pop-up then says why the file
      // cannot roll, rather than nothing happening at all.
      if (completing) setRollPrompt(true);
    }
    catch (e) { alert(`Failed to save ${field}: ${e.message || e}`); }
    finally { setSaving(false); }
  };

  // Rename. TaskNameEditor shows the message itself, so a failure is thrown
  // back to it rather than alerted.
  const renameTask = async (name) => {
    try {
      await apiPatch(`/api/tasks/${id}/`, { name });
    } catch (e) {
      throw new Error(apiErrorMessage(e, 'Could not rename the task.'));
    }
    await reloadTask();
  };

  const toggleCompleted = async () => {
    const becomingCompleted = !task.is_completed;
    const payload = { is_completed: becomingCompleted };
    if (becomingCompleted) {
      const completedStatus = statusOptions.general.find(
        o => (o.name || '').toLowerCase() === 'completed'
      );
      if (completedStatus) payload.status = completedStatus.id;
    }
    setSaving(true);
    try {
      await apiPatch(`/api/tasks/${id}/`, payload);
      await reloadTask();
      // Only after the completion is safely saved. Every category rolls
      // forward. A task with no year to carry still gets the pop-up, which
      // then explains why it cannot roll instead of nothing happening.
      if (becomingCompleted) {
        setRollPrompt(true);
      }
    }
    catch (e) { alert(`Failed to update task: ${e.message || e}`); }
    finally { setSaving(false); }
  };

  // A task with no fiscal_year has no year to close, so there is nothing for
  // the modal to disclose or edit: roll it straight through, as before.
  const rollWithoutYear = async () => {
    try {
      const next = await rollForwardTask(id);
      navigate(`/tasks/${next.id}`);
    } catch (e) {
      alert(`Roll-forward failed: ${apiErrorMessage(e, e.message || String(e))}`);
    }
  };

  // The standalone "Roll Forward to Next Year" button goes through the same
  // modal the completion checkmark opens, so the "this closes the year"
  // disclosure is never bypassed.
  const handleRollForward = () => {
    if (task.fiscal_year) setRollPrompt(true);
    else rollWithoutYear();
  };

  const setPendingField = (field, value) => setPendingChanges(prev => ({ ...prev, [field]: value }));

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    let extras = {};
    if (pendingChanges.gst_status != null) {
      extras = { ...extras, ...computeStatusSideEffects('gst_status', pendingChanges.gst_status) };
    }
    if (pendingChanges.year_end_date) {
      const d = new Date(pendingChanges.year_end_date + 'T00:00:00');
      if (!Number.isNaN(d.getTime())) {
        extras.fiscal_year = d.getFullYear();
      }
    }
    setSaving(true);
    try { await apiPatch(`/api/tasks/${id}/`, { ...pendingChanges, ...extras }); setPendingChanges({}); await reloadTask(); }
    catch (e) { alert(`Save failed: ${e.message || e}`); }
    finally { setSaving(false); }
  };

  const handleFilePicked = async (file) => {
    setPickerOpen(false);
    try {
      await createTaskFile({
        task: id, name: file.name,
        sharepoint_url: file.webUrl || file.url || '',
        sharepoint_id: file.id, file_type: 'sharepoint',
      });
      await reloadFiles();
    } catch (e) { alert(`Failed to link file: ${e.message || e}`); }
  };

  const handleRemoveFile = async (fileId) => {
    if (!window.confirm('Remove this file link?')) return;
    try { await deleteTaskFile(fileId); await reloadFiles(); }
    catch (e) { alert(`Failed to remove file: ${e.message || e}`); }
  };

  if (error) return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;
  if (!task) return <div style={{ padding: 24 }}>Loading…</div>;

  const isCompleted = !!task.is_completed;
  // Finished = the flag OR a "Completed" status (Task.is_finished on the
  // server). 749 older tasks read Completed without the flag, and the
  // server rolls them forward -- so the Roll Forward button must too.
  const isFinished = isCompleted || statusOptions.general.some(
    (o) => o.id === task.status && (o.name || '').trim().toLowerCase() === 'completed');
  const dirtyCount = Object.keys(pendingChanges).length;
  const has = (f) => Object.prototype.hasOwnProperty.call(pendingChanges, f);
  const dispVal = (f) => (has(f) ? pendingChanges[f] : task[f]);
  const taskDisplayName = task.task_type_name || task.name || `Task ${task.id}`;
  const derivedFy = (() => {
    if (task.fiscal_year) return task.fiscal_year;
    const ye = dispVal('year_end_date');
    if (!ye) return null;
    const d = new Date(ye + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  })();

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Top header: back link + client name (large) + status/checkmark split */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
          <Link to="/tasks" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 14 }}>← Tasks</Link>
          {task.client && task.client_name && (
            <Link
              to={`/clients/${task.client}`}
              style={{ color: '#111827', textDecoration: 'none', fontSize: 24, fontWeight: 700 }}
            >
              {task.client_name}
            </Link>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <select
            aria-label="Status"
            value={task.status ?? ''}
            onChange={(e) => patchField('status', e.target.value ? parseInt(e.target.value, 10) : null)}
            disabled={saving}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              background: 'white',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              minWidth: 140,
            }}
          >
            <option value="">— No Status —</option>
            {statusOptions.general.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <button
            onClick={toggleCompleted}
            disabled={saving}
            title={isCompleted ? 'Completed — click to reopen' : 'Mark as completed'}
            style={{
              padding: '10px 14px',
              border: '1px solid ' + (isCompleted ? '#059669' : '#d1d5db'),
              borderRadius: '0 6px 6px 0',
              background: isCompleted ? '#059669' : 'white',
              cursor: saving ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke={isCompleted ? 'white' : '#9ca3af'}
                 strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={tabBtnStyle(tab === t)}>{t}</button>
        ))}
      </div>

      {tab === 'List' && (
        <div>
          {/* Big task name + subtitle + priority */}
          <div style={{ marginBottom: 16 }}>
            <h1 style={{
              margin: 0, fontSize: 28, fontWeight: 700,
              color: isCompleted ? '#9ca3af' : '#111827',
              textDecoration: isCompleted ? 'line-through' : 'none',
            }}>
              {taskDisplayName}
            </h1>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {task.task_category === 'corporate' ? 'Corporate (T2)' : 'Personal (T1)'}
              {' · '}FY {derivedFy || '—'}
              {task.client_name && <> · <span style={{ color: '#9ca3af' }}>{task.client_name}</span></>}
              {saving && <span style={{ marginLeft: 12, color: '#0891b2' }}>Saving…</span>}
            </div>
            <TaskNameEditor
              name={task.name}
              clientName={task.client_name}
              onSave={renameTask}
            />
            {/* Priority is a flag now, not a High/Medium/Low dropdown. */}
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                aria-pressed={!!task.is_flagged}
                onClick={() => patchField('is_flagged', !task.is_flagged)}
                title={task.is_flagged ? 'Flagged — click to clear' : 'Click to flag'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: task.is_flagged ? '#e2445c' : '#f3f4f6',
                  color: task.is_flagged ? 'white' : '#6b7280',
                  padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 800 }}>!</span>
                {task.is_flagged ? 'Flagged' : 'Not flagged'}
              </button>
            </div>
          </div>

          {/* 3-column date cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 24, marginBottom: 24 }}>
            <DateCard label="Year End"
                      value={dispVal('year_end_date')}
                      onChange={(v) => setPendingField('year_end_date', v)}
                      pending={has('year_end_date')} />
            <DateCard label="Docs Received"
                      value={dispVal('docs_received_date')}
                      onChange={(v) => setPendingField('docs_received_date', v)}
                      pending={has('docs_received_date')} />
            <DateCard label="Due Date"
                      value={dispVal('due_date')}
                      onChange={(v) => setPendingField('due_date', v)}
                      pending={has('due_date')}
                      highlightDays={daysFromToday(dispVal('due_date'))} />
          </div>

          {/* Type-specific block */}
          {task.task_category === 'corporate' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={{
                background: 'white',
                border: '1px solid ' + (has('gst_status') ? '#f59e0b' : '#e5e7eb'),
                borderRadius: 8,
                padding: 14,
              }}>
                <div style={dateCardLabel}>GST Status{has('gst_status') ? ' •' : ''}</div>
                <select
                  value={dispVal('gst_status') ?? ''}
                  onChange={(e) => setPendingField('gst_status', e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, background: 'white', cursor: 'pointer' }}
                >
                  <option value="">— None —</option>
                  {statusOptions.tax_filing.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <DateCard label="Efiled Date"
                        value={dispVal('efiled_date')}
                        onChange={(v) => setPendingField('efiled_date', v)}
                        pending={has('efiled_date')} />
            </div>
          )}

          {task.task_category === 'personal' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={{
                background: 'white',
                border: '1px solid ' + (has('efile_number') ? '#f59e0b' : '#e5e7eb'),
                borderRadius: 8,
                padding: 14,
              }}>
                <div style={dateCardLabel}>Efile #{has('efile_number') ? ' •' : ''}</div>
                <input
                  type="number"
                  value={dispVal('efile_number') ?? ''}
                  onChange={(e) => setPendingField('efile_number', e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder="e.g. 1234567890"
                  style={{ width: '100%', padding: '8px 10px', border: '2px solid #d1d5db', borderRadius: 6, fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' }}
                />
              </div>
              <DateCard label="Efile Date"
                        value={dispVal('efile_date')}
                        onChange={(v) => setPendingField('efile_date', v)}
                        pending={has('efile_date')} />
            </div>
          )}

          {/* Billing. Ticked for you when an invoice for this client and year
              leaves draft; still settable by hand, because the firm bills
              outside ClientZone often enough that a derived-only flag would
              read as wrong rather than as missing. */}
          <div style={{ marginTop: 28, display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              id="task-invoiced"
              type="checkbox"
              checked={pendingChanges.invoiced ?? !!task.invoiced}
              onChange={(e) => setPendingField('invoiced', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="task-invoiced" style={{ fontSize: 14, cursor: 'pointer' }}>
              Invoiced
            </label>
            {task.invoiced_on && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {new Date(task.invoiced_on + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </span>
            )}
          </div>

          {/* Action row */}
          <div style={{ marginTop: 32, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={saveChanges}
              disabled={dirtyCount === 0 || saving}
              style={{
                padding: '10px 20px', background: '#059669', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: dirtyCount === 0 ? 'not-allowed' : 'pointer',
                opacity: dirtyCount === 0 ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={handleRollForward} disabled={!isFinished}
              style={{
                padding: '10px 20px', background: 'white', color: '#059669',
                border: '1px solid #059669', borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: isFinished ? 'pointer' : 'not-allowed',
                opacity: isFinished ? 1 : 0.5,
              }}
            >
              Roll Forward to Next Year
            </button>
            {dirtyCount > 0 && <span style={{ color: '#f59e0b', fontSize: 13 }}>You have unsaved changes</span>}
            {!isFinished && <span style={{ color: '#9ca3af', fontSize: 12 }}>Mark task as completed before rolling forward.</span>}
          </div>
          <div style={{ marginTop: 12 }}>
            <TimeLockControl task={task} refreshKey={`${task.is_completed}-${task.next_year?.state}`} />
          </div>
        </div>
      )}

      {tab === 'Files' && (
        <FilesTab
          taskFiles={files}
          clientFiles={task.files || []}
          onLink={() => setPickerOpen(true)}
          onRemove={handleRemoveFile}
        />
      )}
      {tab === 'Time Tracking' && <TimeTrackingTab entries={timeEntries} />}

      <div style={{ marginTop: 32, borderTop: '2px solid #e5e7eb', paddingTop: 24 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, flexWrap: 'wrap', gap: 12,
        }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {viewMode === 'group' ? task.group_name : task.client_name}
          </h2>
          <ViewModeToggle
            mode={viewMode}
            groupName={task.group_name}
            onChange={setViewMode}
            clientLabel="This client"
          />
        </div>

        {groupMembersFailed && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 20,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <strong>Could not load this client&apos;s group.</strong>
            <div style={{ marginTop: 4 }}>
              Group membership failed to load, so &quot;Group: {task.group_name}&quot;
              (if selected) is showing {task.client_name} alone, not the full group.
            </div>
          </div>
        )}

        {rollup.partial && (
          <IncompleteDataBanner
            failedClientIds={rollup.failedClientIds}
            failedFetches={rollup.failedFetches}
          />
        )}

        <MetricTiles
          metrics={rollup.metrics}
          onTileClick={(tile) => setDetailModal(tileDrilldown(tile, rollup))}
        />
        <SummaryByYearTable rows={rollup.yearRows} onCellClick={setDetailModal} />
        <TimeByEmployeePie data={rollup.employeeData} />
        <TimeByCodePie data={rollup.codeData} />

        {viewMode === 'group' && (
          <>
            <GroupDistributionDonut
              data={rollup.clientRows}
              metric={groupMetric}
              onMetricChange={setGroupMetric}
            />
            <BreakdownByClientTable
              rows={rollup.clientRows}
              onClientClick={(clientId) => navigate(`/clients/${clientId}`)}
              onCellClick={setDetailModal}
              timeEntries={rollup.timeEntries}
              invoices={rollup.invoices}
              writeOffs={rollup.writeOffs}
            />
          </>
        )}

        {detailModal && (
          <DetailModal modal={detailModal} onClose={() => setDetailModal(null)} navigate={navigate} />
        )}
      </div>

      <SharePointFilePicker
        isOpen={pickerOpen}
        clientName={task.name || 'this task'}
        onClose={() => setPickerOpen(false)}
        onFileSelected={handleFilePicked}
      />

      {/* Footer: open this task's history timeline */}
      <div style={{
        marginTop: 40, paddingTop: 20, borderTop: '1px solid #e5e7eb',
        display: 'flex', justifyContent: 'center',
      }}>
        <button
          onClick={() => setHistoryOpen(true)}
          style={{
            padding: '10px 18px', border: '1px solid #d1d5db', borderRadius: 8,
            background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            color: '#374151', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          🕑 View history
        </button>
      </div>

      <TimelineDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        recordType="task"
        recordId={id}
        title={`History · ${taskDisplayName}`}
      />

      {rollPrompt && (
        <RollForwardFlow
          task={task}
          onRolled={(next) => { setRollPrompt(false); navigate(`/tasks/${next.id}`); }}
          onClose={() => setRollPrompt(false)}
        />
      )}
    </div>
  );
}

function daysFromToday(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((d - today) / (1000 * 60 * 60 * 24));
}

function DateCard({ label, value, onChange, pending, highlightDays }) {
  return (
    <div style={{
      background: 'white',
      border: '1px solid ' + (pending ? '#f59e0b' : '#e5e7eb'),
      borderRadius: 8,
      padding: 14,
    }}>
      <div style={dateCardLabel}>{label}{pending ? ' •' : ''}</div>
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
      />
      {highlightDays != null && (
        <div style={{
          marginTop: 8,
          fontSize: 12,
          fontWeight: 600,
          color: highlightDays >= 0 ? '#059669' : '#dc2626',
        }}>
          {highlightDays >= 0
            ? `⚠ ${highlightDays} day${highlightDays === 1 ? '' : 's'} until due`
            : `⚠ ${Math.abs(highlightDays)} day${Math.abs(highlightDays) === 1 ? '' : 's'} past due`}
        </div>
      )}
    </div>
  );
}

function FilesTab({ taskFiles, clientFiles, onLink, onRemove }) {
  // Two scopes coexist: task-scoped TaskFile rows, plus the parent client's
  // shared SharePoint files (the same list the Tasks page shows). Client
  // files are read-only here — they're shared across the client's tasks, so
  // they're managed from the Tasks/Client page.
  const rows = [
    ...(taskFiles || []).map(f => ({ ...f, scope: 'task' })),
    ...(clientFiles || []).map(f => ({ ...f, scope: 'client' })),
  ];
  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onLink} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
          + Link SharePoint File
        </button>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>No files linked yet.</div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={th}>Name</th>
                <th style={th}>Scope</th>
                <th style={th}>Source</th>
                <th style={th}>Uploaded by</th>
                <th style={th}>Linked</th>
                <th style={{ ...th, width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(f => {
                const href = f.sharepoint_url || f.file || null;
                const isSharepoint = !!(f.sharepoint_url || f.sharepoint_id);
                const isClient = f.scope === 'client';
                return (
                  <tr key={`${f.scope}-${f.id}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#0073ea', textDecoration: 'none' }}>{f.name || '—'}</a>
                      ) : (f.name || '—')}
                    </td>
                    <td style={td}>
                      {isClient
                        ? <span style={{ color: '#6b7280' }}>Client</span>
                        : <span style={{ color: '#059669' }}>Task</span>}
                    </td>
                    <td style={td}>
                      {isSharepoint ? <span style={{ color: '#1d4ed8' }}>🔗 SharePoint</span> : (f.file ? 'Upload' : (f.file_type || '—'))}
                    </td>
                    <td style={td}>{f.uploaded_by_name || '—'}</td>
                    <td style={td}>{(f.created_at || '').slice(0, 10) || '—'}</td>
                    <td style={td}>
                      {isClient ? (
                        <span style={{ color: '#d1d5db', fontSize: 12 }} title="Client-level file — manage it from the Tasks or Client page">—</span>
                      ) : (
                        <button onClick={() => onRemove(f.id)} title="Remove"
                          style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TimeTrackingTab({ entries }) {
  const list = entries || [];
  const totalHours = list.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const byEmp = {};
  list.forEach(e => { const who = e.user_name || 'Unassigned'; byEmp[who] = (byEmp[who] || 0) + parseFloat(e.hours || 0); });
  const employees = Object.entries(byEmp).sort((a, b) => b[1] - a[1]);
  const horizontal = employees.length <= 3;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={summaryCard}>
          <div style={summaryLabel}>Total hours</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{totalHours.toFixed(2)}</div>
        </div>
        <div style={{ ...summaryCard, flex: 1, minWidth: 240 }}>
          <div style={summaryLabel}>By employee</div>
          {employees.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>No entries yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', flexWrap: 'wrap', gap: horizontal ? 16 : 6, marginTop: 6 }}>
              {employees.map(([who, hrs]) => (
                <div key={who} style={{ fontSize: 13, color: '#374151' }}>
                  <span style={{ fontWeight: 600 }}>{who}</span>
                  <span style={{ color: '#6b7280', marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}>{hrs.toFixed(2)} hrs</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {list.length === 0 ? (
        <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>No time logged yet.</div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={th}>Date</th><th style={th}>Hours</th><th style={th}>Code</th><th style={th}>Who</th><th style={th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {list.map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={td}>{e.date}</td>
                  <td style={td}>{parseFloat(e.hours || 0).toFixed(2)}</td>
                  <td style={td}>{e.time_code || '—'}</td>
                  <td style={td}>{e.user_name || '—'}</td>
                  <td style={td}>{e.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const tabBtnStyle = (active) => ({
  padding: '8px 16px', border: 'none', background: 'transparent',
  borderBottom: active ? '2px solid #059669' : '2px solid transparent',
  color: active ? '#059669' : '#6b7280',
  fontWeight: active ? 600 : 500, cursor: 'pointer', fontSize: 14,
});

const dateCardLabel = { fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '10px 12px', fontSize: 13 };
const summaryCard = { background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 18px', minWidth: 160 };
const summaryLabel = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
